// FE-SCRM-060 — <CrmFormBanner> top-of-modal validation summary.
//
// Example:
//
//     <CrmFormBanner errors={banner} />
//     // ``banner`` is normalized by the host modal:
//     //   [{ field: "name", message: "Name is required" },
//     //    { message: "Server returned 500" }]
//     // Field-scoped errors render as #field-<name> anchor links;
//     // form-scoped (no field) render as inert text.
//
// Renders ``null`` when ``errors`` is empty so the host doesn't
// need to conditionally mount it.

import { useCallback } from "react";


export interface FormBannerError {
  /** Field id slug. Anchors the link to ``#field-<field>``. */
  field?: string;
  message: string;
}


export interface CrmFormBannerProps {
  errors: readonly FormBannerError[];
  /** Fired before the page scrolls to the anchor. The host can
   * additionally focus the input or expand a collapsed section. */
  onFieldClick?: (field: string) => void;
  className?: string;
}


export function CrmFormBanner({
  errors, onFieldClick, className,
}: CrmFormBannerProps): JSX.Element | null {
  const handleClick = useCallback(
    (field: string) => (e: React.MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault();
      onFieldClick?.(field);
      // Scroll the offending input into view regardless of whether
      // the consumer wired up onFieldClick.
      document.getElementById(`field-${field}`)?.scrollIntoView({
        behavior: "smooth", block: "center",
      });
    },
    [onFieldClick],
  );

  if (errors.length === 0) return null;

  const renderItem = (err: FormBannerError, idx: number) => (
    <li key={`${err.field ?? "_form"}-${idx}`}>
      {err.field ? (
        <a
          href={`#field-${err.field}`}
          onClick={handleClick(err.field)}
          className="underline underline-offset-2 hover:text-rose-900"
        >
          {err.message}
        </a>
      ) : (
        <span>{err.message}</span>
      )}
    </li>
  );

  return (
    <div
      role="alert"
      className={
        "rounded-md border border-rose-200 bg-rose-50 px-3 py-2 "
        + "text-sm text-rose-800 "
        + (className ?? "")
      }
    >
      <p className="font-medium">
        {errors.length === 1
          ? "Fix the following before continuing:"
          : `Fix the following ${errors.length} issues before continuing:`}
      </p>
      <ul className="mt-1 list-inside list-disc space-y-0.5">
        {errors.map(renderItem)}
      </ul>
    </div>
  );
}
